import { Box, Image, Text, VStack } from '@chakra-ui/react'
import { useDaily, useLocalSessionId } from '@daily-co/daily-react'
import React, { useEffect, useRef, useState } from 'react'
import { useTelehealthControllerV1PostCheckSessionStarted } from '~/public-api'
import { PinnedTile } from './components'
import { useActiveTelehealthSession } from './context/active-telehealth-session.context'
import { usePatientDailyManager } from './context/patient-daily-manager.context'

export const ClientReadyToJoin = ({
  clinicianDisplayId,
  patientCode,
  email
}: {
  clinicianDisplayId: string
  patientCode?: string | undefined
  email?: string | undefined
}) => {
  const [dailyRoomUrl, setDailyRoomUrl] = useState<string | null>(null)
  const [localAspectRatio, setLocalAspectRatio] = useState(16 / 9)
  const attemptRef = useRef(0)
  const localSessionId = useLocalSessionId()
  const {
    configureRoom,
    requestAccessToJoinCall,
    isInCall,
    roomConfiguration,
    participantFullName,
    isReadyToJoin,
    hasLeftCall,
    isCallEnded
  } = usePatientDailyManager()
  const daily = useDaily()

  const isLocalPortrait = localAspectRatio && localAspectRatio < 1
  const width = {
    base: isLocalPortrait ? '90px' : '150px',
    sm: '200px',
    md: '300px'
  }

  const { mutate: checkSessionStarted } =
    useTelehealthControllerV1PostCheckSessionStarted()

  const { setRecordingStartedAt } = useActiveTelehealthSession()

  useEffect(() => {
    if (isInCall || hasLeftCall || isCallEnded) {
      return
    }

    const checkSession = () => {
      checkSessionStarted(
        {
          data: {
            clinicianDisplayId,
            email,
            patientCode,
            attempt: attemptRef.current,
            participantFullName
          }
        },
        {
          onSuccess: async response => {
            if (response.dailyRoomUrl) {
              setDailyRoomUrl(response.dailyRoomUrl)
              setRecordingStartedAt(response.recordingStartedAt)
            } else {
              setDailyRoomUrl(null)
            }
            attemptRef.current = attemptRef.current + 1
          }
        }
      )
    }

    checkSession()

    const interval = setInterval(() => {
      checkSession()
    }, 5000)

    return () => clearInterval(interval)
  }, [
    checkSessionStarted,
    clinicianDisplayId,
    patientCode,
    isInCall,
    email,
    hasLeftCall,
    isCallEnded,
    setRecordingStartedAt,
    participantFullName
  ])

  useEffect(() => {
    if (dailyRoomUrl) {
      configureRoom({ url: dailyRoomUrl })
    } else {
      if (daily?.meetingState() !== 'joined-meeting') {
        daily?.startCamera()
      }

      const isBackgroundBlurred =
        localStorage.getItem('telehealth_background_blur') === 'true'
      daily?.updateInputSettings({
        video: {
          processor: isBackgroundBlurred
            ? { type: 'background-blur', config: { strength: 0.9 } }
            : { type: 'none' }
        }
      })
    }
  }, [dailyRoomUrl])

  useEffect(() => {
    if (isReadyToJoin && roomConfiguration.url) {
      requestAccessToJoinCall({ name: participantFullName || 'Client' })
    }
  }, [roomConfiguration.url, isReadyToJoin, participantFullName])

  return (
    <Box w="100%" h="100%" position="relative">
      <PinnedTile
        width={width}
        participantId={localSessionId}
        aspectRatio={localAspectRatio}
        setAspectRatio={setLocalAspectRatio}
      />
      <VStack h="100%" w="100%" spacing="small" justify="center" pt="72px">
        <Text fontSize="24px" color="#C0CCF8">
          Waiting to start...
        </Text>
        <Text textAlign="center" w="265px" fontSize="16px" color="#A1A1A1">
          The session will start automatically once your therapist is ready.
        </Text>
        <Image
          w="530px"
          h="298px"
          src="https://res.cloudinary.com/hellojoy/image/upload/v1748018930/gifs/waiting-room-chair-loop-compressed_2.gif"
          alt="Waiting to start"
          objectFit="cover"
        />
      </VStack>
    </Box>
  )
}
